/* Keep the native chat launcher clear of purchase controls and store details. */
(() => {
  'use strict';
  if (!document.body.classList.contains('bv-product-page')) return;

  const criticalSelector = [
    '.bv-hero--cro [data-bv-atc]',
    '.bv-hero--cro .bv-variant',
    '.bv-hero--cro [data-bv-addon]',
    '.bv-hero--cro .bv-offer-note',
    '[data-bv-sticky].is-visible :is([data-bv-sticky-trigger], [data-bv-sticky-selection], .bv-sticky-title, .bv-sticky-line)',
    'product-info[id^="MainProduct-"] .product-form__buttons',
    'product-info[id^="MainProduct-"] .product-form__quantity',
    'product-info[id^="MainProduct-"] variant-selects',
    '.bv-store-trust__address'
  ].join(',');
  let host;
  let frame = 0;
  let discovery;
  let startupTimer = 0;
  let startupAttempt = 0;
  const startupDelays = [250, 1000, 4000, 12000, 30000];
  const observedRoots = new WeakSet();
  const observedSizes = new WeakSet();
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

  function schedule() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  function observeSize(element) {
    if (resize && !observedSizes.has(element)) {
      observedSizes.add(element);
      resize.observe(element);
    }
  }

  // Read the app's exported parts through open shadow roots. Never change its
  // private markup, inline styles, listeners, focus or dialog state.
  function nativeParts(root, result) {
    if (!observedRoots.has(root)) {
      observedRoots.add(root);
      new MutationObserver(schedule).observe(root, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['aria-expanded', 'aria-hidden', 'hidden', 'open', 'style', 'class']
      });
    }
    root.querySelectorAll('[part~="activator"]').forEach(element => result.activators.push(element));
    root.querySelectorAll('[part~="dialog"], [role="dialog"]').forEach(element => result.dialogs.push(element));
    root.querySelectorAll('*').forEach(element => {
      if (element.shadowRoot) nativeParts(element.shadowRoot, result);
    });
    return result;
  }

  function parentElement(element) {
    return element.parentElement || element.getRootNode().host || null;
  }

  function visible(element) {
    if (!element.isConnected || !element.getClientRects().length) return false;
    for (let current = element; current; current = parentElement(current)) {
      const style = getComputedStyle(current);
      if (current.hidden || current.getAttribute('aria-hidden') === 'true' ||
          style.display === 'none' || style.visibility === 'hidden' ||
          style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    return true;
  }

  function overlaps(a, b) {
    return a.right > b.left && a.left < b.right && a.bottom > b.top && a.top < b.bottom;
  }

  function update() {
    frame = 0;
    if (!host || !host.isConnected) return findHost();
    const parts = nativeParts(host.shadowRoot || host, {activators: [], dialogs: []});
    if (parts.activators.length) {
      clearTimeout(startupTimer);
      startupTimer = 0;
      startupAttempt = startupDelays.length;
    } else if (!startupTimer && startupAttempt < startupDelays.length) {
      // A late attachShadow() does not notify a light-DOM observer. Retry only
      // during startup, with backoff; stop permanently when the launcher exists.
      startupTimer = setTimeout(() => {
        startupTimer = 0;
        schedule();
      }, startupDelays[startupAttempt++]);
    }
    const chatOpen = host.matches(':focus-within') ||
      parts.activators.some(element => element.getAttribute('aria-expanded') === 'true') ||
      parts.dialogs.some(visible);
    host.toggleAttribute('data-bv-chat-open', chatOpen);
    if (chatOpen) {
      host.removeAttribute('data-bv-launcher-overlap');
      return;
    }

    // Visibility:hidden preserves the launcher's actual geometry while hidden,
    // preventing a hide/show oscillation. No guessed mobile offsets are used.
    const areas = parts.activators.map(element => {
      observeSize(element);
      return element.getBoundingClientRect();
    }).filter(rect => rect.width > 0 && rect.height > 0);
    const viewport = {left: 0, top: 0, right: innerWidth, bottom: innerHeight};
    const targets = [...document.querySelectorAll(criticalSelector)];
    const blocked = targets.some(element => {
      observeSize(element);
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && overlaps(rect, viewport) &&
        areas.some(area => overlaps(area, viewport) && overlaps(rect, area)) && visible(element);
    });
    host.toggleAttribute('data-bv-launcher-overlap', blocked);
  }

  function findHost() {
    host = document.querySelector('shopify-chat');
    if (host) {
      if (discovery) discovery.disconnect();
      discovery = null;
      observeSize(host);
      schedule();
    } else if (!discovery) {
      // This discovery observer stops as soon as the asynchronously loaded
      // app host appears. Ongoing observation stays inside the chat component.
      discovery = new MutationObserver(findHost);
      discovery.observe(document.body, {childList: true, subtree: true});
    }
  }

  window.addEventListener('scroll', schedule, {passive: true});
  window.addEventListener('resize', schedule, {passive: true});
  window.visualViewport?.addEventListener('resize', schedule, {passive: true});
  window.visualViewport?.addEventListener('scroll', schedule, {passive: true});
  document.addEventListener('focusin', schedule);
  document.addEventListener('focusout', schedule);
  document.addEventListener('change', schedule);
  document.addEventListener('load', schedule, true);
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('shopify:section:load', schedule);
  if (document.fonts?.ready) document.fonts.ready.then(schedule);
  if (window.customElements) customElements.whenDefined('shopify-chat').then(schedule);
  findHost();
})();

/* Let the native popup finish opening before replaying an early user close. */
(() => {
  'use strict';
  const formSelector = 'form[data-testid="klaviyo-form-X2B2R8"]';
  let queued = null;
  function currentDialog() {
    const forms = document.querySelectorAll(formSelector);
    return forms.length === 1 ? forms[0].closest('[role="dialog"]') : null;
  }
  function opening(dialog) {
    if (typeof dialog.getAnimations !== 'function') return null;
    const matches = dialog.getAnimations().filter(animation => animation.animationName === 'klaviyo-fadein' &&
      animation.effect?.target === dialog && !animation.effect.pseudoElement);
    return matches.length === 1 ? matches[0] : null;
  }
  function cancel() {
    if (queued?.frame) cancelAnimationFrame(queued.frame);
    if (queued?.dialog.isConnected) queued.dialog.setAttribute('data-bv-early-close', 'cancelled');
    queued = null;
  }
  document.addEventListener('click', event => {
    if (!event.isTrusted) return;
    const button = event.target.closest?.('button'), dialog = currentDialog();
    if (!button || !dialog?.contains(button)) return;
    const close = button.classList.contains('klaviyo-close-form') && button.getAttribute('aria-label') === 'Close dialog';
    const decline = button.type === 'button' && button.getAttribute('data-action-id') === '01M24PH7X5RVYNAMWRSQHGQHJ4';
    if (!close && !decline) { if (queued?.dialog === dialog) cancel(); return; }
    const animation = opening(dialog);
    if (!animation || (!animation.pending && animation.playState !== 'running') || !animation.finished?.then) {
      if (queued?.dialog === dialog) cancel();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (queued?.dialog === dialog && queued.animation === animation) return;
    cancel();
    const record = queued = {dialog, button, animation, frame: 0};
    dialog.setAttribute('data-bv-early-close', 'queued');
    animation.finished.then(() => {
      if (queued !== record) return;
      // Two frames let the vendor's opening-end handlers finish before its close.
      record.frame = requestAnimationFrame(() => {
        record.frame = requestAnimationFrame(() => {
          if (queued !== record) return;
          const style = getComputedStyle(dialog), current = opening(dialog);
          if (!dialog.isConnected || currentDialog() !== dialog || !button.isConnected ||
              !dialog.contains(button) || button.disabled || (current && current !== animation) ||
              Number(style.opacity) < 0.999 || style.display === 'none' || style.visibility !== 'visible' ||
              style.animationName.split(',').some(name => name.trim() === 'klaviyo-fadeout')) { cancel(); return; }
          queued = null;
          dialog.setAttribute('data-bv-early-close', 'replayed');
          button.click(); // Untrusted replay bypasses this capture handler; native close runs once.
        });
      });
    }, () => { if (queued === record) cancel(); });
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.matches?.(formSelector) && queued?.dialog === event.target.closest('[role="dialog"]')) cancel();
  }, true);
  window.addEventListener('pagehide', cancel);
})();
